import { useAuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { AccountVerificationRequired, ContainerDefault, ErrorMessage, WebinarNotPublishedWarning } from '@core/ui';
import { getRouteApi, useLocation, useNavigate } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WebinarInfo, WebinarViewerActions } from '../../organisms';

declare module '@tanstack/history' {
    export interface HistoryState {
        showInterest?: boolean;
    }
}

const route = getRouteApi('/$webinarId');
export const WebinarDetailsPage = () => {
    const { webinarId } = route.useParams();
    const { t } = useTranslation();

    const { state } = useLocation();
    const navigate = useNavigate();

    const { isAuthenticated, isVerified } = useAuthContext();

    const { data: webinar, refetch: refetchWebinar } = useSuspenseQuery(OptionsApi.interest.getStatus(webinarId!, { staleTime: 60_000 }));
    const { data: webinarAttachments } = useSuspenseQuery(OptionsApi.webinarAttachments.get(webinarId!, { staleTime: 60_000 }));

    const showInterestMutation = useMutation(OptionsApi.interest.showInterest());
    const retractInterestMutation = useMutation(OptionsApi.interest.retractInterest());
    const showInterest = () => {
        if (isAuthenticated) {
            showInterestMutation.mutate(webinarId!, {
                onSuccess: () => refetchWebinar()
            });
        } else {
            navigate({ to: '/$webinarId/login', params: { webinarId }, state: { showInterest: true } });
        }
    };

    const retractInterest = () => {
        retractInterestMutation.mutate(webinarId!, {
            onSuccess: () => refetchWebinar()
        });
    };

    useEffect(() => {
        if (isAuthenticated && !!state?.showInterest) {
            showInterest();
        }
    }, [isAuthenticated, state]);

    useEffect(() => {
        if (isAuthenticated) {
            refetchWebinar();
        }
    }, [isAuthenticated]);

    const mutationError = showInterestMutation.error ?? retractInterestMutation.error;
    return (
        <ContainerDefault>
            <div data-testid="broadcastPage">
                {isAuthenticated && <BackLink to="/">{t('WebinarDetails.Link.BackToOverview')}</BackLink>}
                {mutationError && (
                    <div className="max-w-4xl">
                        <ErrorMessage apiError={mutationError} />
                    </div>
                )}
                <div className="max-w-4xl">
                    {isAuthenticated && !isVerified && webinar.requiresAccountVerification && (
                        <div className="mb-4">
                            <AccountVerificationRequired />
                        </div>
                    )}
                    {!webinar.isPublished && (
                        <div className="mb-4">
                            <WebinarNotPublishedWarning />
                        </div>
                    )}
                    <WebinarInfo webinar={webinar} webinarAttachments={webinarAttachments} />
                    <WebinarViewerActions subscribe={showInterest} unsubscribe={retractInterest} webinar={webinar} isLoading={false} />
                </div>
            </div>
        </ContainerDefault>
    );
};
